/*
 * Copyright 2026 Sophos Limited. All rights reserved.
 *
 * 'Sophos' and 'Sophos Anti-Virus' are registered trademarks of Sophos Limited and Sophos Group. All other product
 * and company names mentioned are trademarks or registered trademarks of their respective owners.
 */
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState, type ReactElement } from "react";
import { type ErrorsResult } from "../../services/dailyChecksApi";

type PipelineErrorsTableProps = {
  /** Process pipeline errors fetched from Logzio */
  data: ErrorsResult;
};

const PAGE_SIZE = 15;

/** Renders pipeline errors from the last 24 hours with pagination */
export function PipelineErrorsTable({ data }: PipelineErrorsTableProps): ReactElement {
  const [page, setPage] = useState(0);

  const totalPages = Math.ceil(data.errors.length / PAGE_SIZE);
  const pageErrors = data.errors.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Box>
      <TableContainer>
        <Table size="small" aria-label="pipeline-errors">
          <TableHead>
            <TableRow>
              <TableCell>Message</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Organisation</TableCell>
              <TableCell align="right">Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageErrors.map((err, i) => (
              <TableRow key={i}>
                <TableCell sx={{ wordBreak: "break-word" }}>{err.message}</TableCell>
                <TableCell>{err.region}</TableCell>
                <TableCell>{err.organisationIdVarious ? "Various" : err.organisationId ?? "—"}</TableCell>
                <TableCell align="right">{err.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.errors.length)} of {data.errors.length}
          </Typography>
          <Box display="flex" gap={1}>
            <Button size="small" variant="outlined" disabled={page === 0} onClick={() => setPage(page - 1)}>
              ← Prev
            </Button>
            <Button size="small" variant="outlined" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
              Next →
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
